'use client'

import { CSSProperties, FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { extendedKanaGroupIds, hiraganaTable, katakanaTable, vowels } from '../alphabet/kana-data'
import {
    getEndingVowel,
    getKanaAudioPath,
    isSameKana,
    normalizeKanaInput,
    normalizeRomaji,
    type SelectedKana
} from '../_lib/kana-practice'
import { Button } from '~/components/ui/button'
import { Checkbox } from '~/components/ui/checkbox'
import { Input } from '~/components/ui/input'
import { cn } from '~/lib/utils'

type ScriptMode = 'hiragana' | 'katakana' | 'both'
type KanaPoolMode = 'base' | 'yoon' | 'both'
type QuizDirection = 'kana-to-romaji' | 'romaji-to-kana'

type TypeRandomSettings = {
    scriptMode: ScriptMode
    kanaPoolMode: KanaPoolMode
    selectedVowels: string[]
    selectedExtendedGroupIds: string[]
    quizDirection: QuizDirection
}

type ResultState = 'idle' | 'correct' | 'wrong'

type QuizStats = {
    total: number
    correct: number
    wrong: number
}

const settingsStorageKey = 'kana-type-random-settings-v1'
const extendedGroupOptions = katakanaTable.extendedRows.map((group) => ({
    id: group.id,
    label: group.label
}))
const defaultSettings: TypeRandomSettings = {
    scriptMode: 'both',
    kanaPoolMode: 'base',
    selectedVowels: [...vowels],
    selectedExtendedGroupIds: [...extendedKanaGroupIds],
    quizDirection: 'kana-to-romaji'
}

const confettiColors = ['#ef4444', '#f59e0b', '#10b981', '#3b82f6', '#ec4899', '#a855f7']
const confettiPieceIndexes = Array.from({ length: 18 }, (_, index) => index)

function createConfettiPieceStyle(index: number, side: 'left' | 'right'): CSSProperties {
    const x = 84 + (index % 6) * 16
    const y = -160 - (index % 5) * 24
    const rotationDirection = side === 'left' ? 1 : -1
    const rotation = rotationDirection * (180 + index * 18)

    return {
        backgroundColor: confettiColors[index % confettiColors.length],
        animationDelay: `${(index % 6) * 0.03}s`,
        '--confetti-x': `${x}px`,
        '--confetti-y': `${y}px`,
        '--confetti-rot': `${rotation}deg`
    } as CSSProperties
}

function SideConfetti({ burstId }: { burstId: number }) {
    return (
        <>
            <div key={`confetti-${burstId}`} className='side-confetti-overlay'>
                <div className='side-confetti-cannon side-confetti-left'>
                    {confettiPieceIndexes.map((index) => (
                        <span
                            key={`confetti-left-${burstId}-${index}`}
                            className='side-confetti-piece side-confetti-piece-left'
                            style={createConfettiPieceStyle(index, 'left')}
                        />
                    ))}
                </div>
                <div className='side-confetti-cannon side-confetti-right'>
                    {confettiPieceIndexes.map((index) => (
                        <span
                            key={`confetti-right-${burstId}-${index}`}
                            className='side-confetti-piece side-confetti-piece-right'
                            style={createConfettiPieceStyle(index, 'right')}
                        />
                    ))}
                </div>
            </div>
            <style jsx global>{`
                .side-confetti-overlay {
                    pointer-events: none;
                    position: fixed;
                    inset: 0;
                    z-index: 60;
                    overflow: hidden;
                }

                .side-confetti-cannon {
                    position: absolute;
                    top: 52%;
                    display: flex;
                    gap: 6px;
                }

                .side-confetti-left {
                    left: 16px;
                }

                .side-confetti-right {
                    right: 16px;
                }

                .side-confetti-piece {
                    height: 10px;
                    width: 6px;
                    border-radius: 2px;
                    opacity: 0;
                }

                .side-confetti-piece-left {
                    animation: side-confetti-left 900ms cubic-bezier(0.1, 0.8, 0.2, 1) forwards;
                }

                .side-confetti-piece-right {
                    animation: side-confetti-right 900ms cubic-bezier(0.1, 0.8, 0.2, 1) forwards;
                }

                @keyframes side-confetti-left {
                    0% {
                        opacity: 1;
                        transform: translate(0, 0) rotate(0deg) scale(1);
                    }
                    100% {
                        opacity: 0;
                        transform: translate(var(--confetti-x), var(--confetti-y)) rotate(var(--confetti-rot)) scale(0.6);
                    }
                }

                @keyframes side-confetti-right {
                    0% {
                        opacity: 1;
                        transform: translate(0, 0) rotate(0deg) scale(1);
                    }
                    100% {
                        opacity: 0;
                        transform: translate(calc(var(--confetti-x) * -1), var(--confetti-y)) rotate(var(--confetti-rot))
                            scale(0.6);
                    }
                }
            `}</style>
        </>
    )
}

export default function TypeRandomClient() {
    const [settings, setSettings] = useState<TypeRandomSettings>(defaultSettings)
    const [question, setQuestion] = useState<SelectedKana | null>(null)
    const [answer, setAnswer] = useState('')
    const [resultState, setResultState] = useState<ResultState>('idle')
    const [resultMessage, setResultMessage] = useState('')
    const [showAnswer, setShowAnswer] = useState(false)
    const [stats, setStats] = useState<QuizStats>({ total: 0, correct: 0, wrong: 0 })
    const [isSettingsReady, setIsSettingsReady] = useState(false)
    const [isAudioPlaying, setIsAudioPlaying] = useState(false)
    const [audioError, setAudioError] = useState('')
    const [isSettingsVisible, setIsSettingsVisible] = useState(true)
    const [confettiBurstId, setConfettiBurstId] = useState(0)
    const [showConfetti, setShowConfetti] = useState(false)
    const audioRef = useRef<HTMLAudioElement | null>(null)
    const confettiTimerRef = useRef<number | null>(null)
    const answerInputRef = useRef<HTMLInputElement | null>(null)

    const { scriptMode, kanaPoolMode, selectedVowels, selectedExtendedGroupIds, quizDirection } = settings

    const activeTables = useMemo(() => {
        if (scriptMode === 'hiragana') {
            return [hiraganaTable]
        }
        if (scriptMode === 'katakana') {
            return [katakanaTable]
        }
        return [hiraganaTable, katakanaTable]
    }, [scriptMode])

    const randomPool = useMemo(() => {
        const collected: SelectedKana[] = []
        const includeBase = kanaPoolMode === 'base' || kanaPoolMode === 'both'
        const includeYoon = kanaPoolMode === 'yoon' || kanaPoolMode === 'both'

        for (const table of activeTables) {
            if (includeBase) {
                for (const row of table.baseRows) {
                    for (const cell of row) {
                        if (!cell.kana) {
                            continue
                        }

                        const endingVowel = getEndingVowel(cell.romaji)
                        if (!endingVowel || !selectedVowels.includes(endingVowel)) {
                            continue
                        }

                        collected.push({ cell, script: table.title })
                    }
                }
            }

            if (includeYoon) {
                for (const row of table.yoonRows) {
                    for (const cell of row) {
                        if (cell.kana) {
                            collected.push({ cell, script: table.title })
                        }
                    }
                }
            }

            for (const extendedRow of table.extendedRows) {
                if (!selectedExtendedGroupIds.includes(extendedRow.id)) {
                    continue
                }

                for (const cell of extendedRow.cells) {
                    if (cell.kana) {
                        collected.push({ cell, script: table.title })
                    }
                }
            }
        }

        return collected
    }, [activeTables, kanaPoolMode, selectedExtendedGroupIds, selectedVowels])

    const audioPath = useMemo(() => {
        if (!question) {
            return null
        }
        return getKanaAudioPath(question)
    }, [question])

    const pickRandomKana = useCallback((pool: SelectedKana[], current: SelectedKana | null) => {
        if (pool.length === 0) {
            return null
        }

        if (pool.length === 1) {
            return pool[0]
        }

        const maxRetry = 8
        let next = pool[Math.floor(Math.random() * pool.length)]
        let tries = 0

        while (isSameKana(next, current) && tries < maxRetry) {
            next = pool[Math.floor(Math.random() * pool.length)]
            tries += 1
        }

        return next
    }, [])

    const resetAnswerState = useCallback(() => {
        setAnswer('')
        setResultState('idle')
        setResultMessage('')
        setShowAnswer(false)
        setAudioError('')
    }, [])

    const focusAnswerInput = useCallback(() => {
        window.requestAnimationFrame(() => {
            answerInputRef.current?.focus()
        })
    }, [])

    const handleNextQuestion = useCallback(() => {
        if (randomPool.length === 0) {
            setQuestion(null)
            resetAnswerState()
            return
        }

        setQuestion((current) => pickRandomKana(randomPool, current))
        resetAnswerState()
        focusAnswerInput()
    }, [focusAnswerInput, pickRandomKana, randomPool, resetAnswerState])

    const toggleVowel = (vowel: string) => {
        setSettings((current) => {
            if (current.selectedVowels.includes(vowel)) {
                return {
                    ...current,
                    selectedVowels: current.selectedVowels.filter((item) => item !== vowel)
                }
            }

            return {
                ...current,
                selectedVowels: [...current.selectedVowels, vowel]
            }
        })
    }

    const toggleExtendedGroup = (groupId: string) => {
        setSettings((current) => {
            if (current.selectedExtendedGroupIds.includes(groupId)) {
                return {
                    ...current,
                    selectedExtendedGroupIds: current.selectedExtendedGroupIds.filter((item) => item !== groupId)
                }
            }

            return {
                ...current,
                selectedExtendedGroupIds: [...current.selectedExtendedGroupIds, groupId]
            }
        })
    }

    const handlePlayAudio = useCallback(() => {
        if (!question || !audioPath || isAudioPlaying) {
            return
        }

        const audio = audioRef.current ?? new Audio()
        audio.src = audioPath
        audio.currentTime = 0
        audio.onended = () => setIsAudioPlaying(false)
        audio.onerror = () => setIsAudioPlaying(false)
        audioRef.current = audio

        setAudioError('')
        setIsAudioPlaying(true)
        audio.play().catch(() => {
            setIsAudioPlaying(false)
            setAudioError('Không thể phát audio. Trình duyệt đã chặn hoặc file bị lỗi.')
        })
    }, [audioPath, isAudioPlaying, question])

    const triggerConfetti = useCallback(() => {
        if (confettiTimerRef.current) {
            window.clearTimeout(confettiTimerRef.current)
            confettiTimerRef.current = null
        }

        setConfettiBurstId((current) => current + 1)
        setShowConfetti(true)
        confettiTimerRef.current = window.setTimeout(() => {
            setShowConfetti(false)
            confettiTimerRef.current = null
        }, 1200)
    }, [])

    const playCorrectAudioThenNext = useCallback(
        (correctQuestion: SelectedKana) => {
            const correctAudioPath = getKanaAudioPath(correctQuestion)

            if (!correctAudioPath) {
                handleNextQuestion()
                return
            }

            const audio = audioRef.current ?? new Audio()
            audio.src = correctAudioPath
            audio.currentTime = 0
            audio.onended = () => {
                setIsAudioPlaying(false)
                handleNextQuestion()
            }
            audio.onerror = () => {
                setIsAudioPlaying(false)
                setAudioError('Không thể phát audio. Trình duyệt đã chặn hoặc file bị lỗi.')
                handleNextQuestion()
            }
            audioRef.current = audio

            setAudioError('')
            setIsAudioPlaying(true)
            audio.play().catch(() => {
                setIsAudioPlaying(false)
                setAudioError('Không thể phát audio. Trình duyệt đã chặn hoặc file bị lỗi.')
                handleNextQuestion()
            })
        },
        [handleNextQuestion]
    )

    const checkAnswer = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()

        if (!question) {
            return
        }

        const value = answer.trim()
        if (!value) {
            setResultState('wrong')
            setResultMessage('Bạn chưa nhập đáp án.')
            setShowAnswer(false)
            focusAnswerInput()
            return
        }

        const isCorrect =
            quizDirection === 'kana-to-romaji'
                ? normalizeRomaji(value) === normalizeRomaji(question.cell.romaji)
                : normalizeKanaInput(value) === question.cell.kana

        setStats((current) => ({
            total: current.total + 1,
            correct: current.correct + (isCorrect ? 1 : 0),
            wrong: current.wrong + (isCorrect ? 0 : 1)
        }))

        if (isCorrect) {
            setResultState('correct')
            setResultMessage('Chính xác. Đang phát âm và chuyển câu mới...')
            setShowAnswer(true)
            triggerConfetti()
            playCorrectAudioThenNext(question)
            return
        }

        setResultState('wrong')
        setResultMessage('Chưa đúng, xem đáp án bên dưới.')
        setShowAnswer(true)
        focusAnswerInput()
    }

    useEffect(() => {
        const rawSettings = window.localStorage.getItem(settingsStorageKey)

        if (!rawSettings) {
            setIsSettingsReady(true)
            return
        }

        try {
            const parsed = JSON.parse(rawSettings) as Partial<TypeRandomSettings>
            const nextSettings: TypeRandomSettings = { ...defaultSettings }

            if (parsed.scriptMode && ['hiragana', 'katakana', 'both'].includes(parsed.scriptMode)) {
                nextSettings.scriptMode = parsed.scriptMode
            }

            if (parsed.kanaPoolMode && ['base', 'yoon', 'both'].includes(parsed.kanaPoolMode)) {
                nextSettings.kanaPoolMode = parsed.kanaPoolMode
            }

            if (parsed.quizDirection && ['kana-to-romaji', 'romaji-to-kana'].includes(parsed.quizDirection)) {
                nextSettings.quizDirection = parsed.quizDirection
            }

            if (Array.isArray(parsed.selectedVowels)) {
                const nextVowels = parsed.selectedVowels.filter(
                    (item): item is string => typeof item === 'string' && vowels.includes(item)
                )
                nextSettings.selectedVowels = Array.from(new Set(nextVowels))
            }

            if (Array.isArray(parsed.selectedExtendedGroupIds)) {
                const nextExtendedGroups = parsed.selectedExtendedGroupIds.filter(
                    (item): item is string =>
                        typeof item === 'string' &&
                        extendedKanaGroupIds.includes(item as (typeof extendedKanaGroupIds)[number])
                )
                nextSettings.selectedExtendedGroupIds = Array.from(new Set(nextExtendedGroups))
            }

            setSettings(nextSettings)
        } catch {
            // Ignore invalid localStorage payload.
        } finally {
            setIsSettingsReady(true)
        }
    }, [])

    useEffect(() => {
        if (!isSettingsReady) {
            return
        }

        window.localStorage.setItem(settingsStorageKey, JSON.stringify(settings))
    }, [isSettingsReady, settings])

    useEffect(() => {
        if (!question) {
            return
        }

        const inPool = randomPool.some((item) => isSameKana(item, question))
        if (!inPool) {
            setQuestion(null)
            resetAnswerState()
        }
    }, [question, randomPool, resetAnswerState])

    useEffect(() => {
        if (question || randomPool.length === 0) {
            return
        }

        setQuestion(pickRandomKana(randomPool, null))
    }, [pickRandomKana, question, randomPool])

    useEffect(() => {
        if (!question || isAudioPlaying) {
            return
        }

        focusAnswerInput()
    }, [focusAnswerInput, isAudioPlaying, question])

    useEffect(() => {
        return () => {
            if (audioRef.current) {
                audioRef.current.pause()
                audioRef.current.onended = null
                audioRef.current.onerror = null
                audioRef.current = null
            }

            if (confettiTimerRef.current) {
                window.clearTimeout(confettiTimerRef.current)
                confettiTimerRef.current = null
            }
        }
    }, [])

    useEffect(() => {
        setAudioError('')
    }, [question?.cell.kana, question?.script])

    const promptValue =
        question && (quizDirection === 'kana-to-romaji' ? question.cell.kana : normalizeRomaji(question.cell.romaji))
    const panelClass = 'rounded-xl border border-border bg-card p-4 shadow-sm sm:p-6'
    const subPanelClass = 'rounded-lg border border-border bg-muted/40 p-3'
    const titleClass = 'text-sm font-semibold text-foreground'
    const subtleTextClass = 'text-sm text-muted-foreground'
    const dangerTextClass = 'text-sm text-destructive'

    return (
        <main className='mx-auto w-full max-w-(--breakpoint-xl) px-4 py-6 sm:px-6 sm:py-8'>
            {showConfetti && <SideConfetti burstId={confettiBurstId} />}
            <div className='mb-6 text-center sm:mb-8'>
                <h1 className='text-2xl font-bold text-foreground sm:text-3xl'>Luyện gõ Kana ngẫu nhiên</h1>
                <p className='mt-2 text-sm text-muted-foreground sm:text-base'>
                    Chọn bộ ký tự ngẫu nhiên theo bảng bạn muốn rồi nhập đáp án theo chiều Kana - Romaji hoặc Romaji - Kana.
                </p>
            </div>

            <section className={cn(panelClass, 'mb-6')}>
                <div className='flex items-center justify-between gap-2'>
                    <h2 className='text-lg font-semibold text-foreground'>Cài đặt</h2>
                    <Button
                        type='button'
                        onClick={() => setIsSettingsVisible((current) => !current)}
                        variant='outline'
                        size='sm'
                    >
                        {isSettingsVisible ? 'Ẩn cài đặt' : 'Hiện cài đặt'}
                    </Button>
                </div>

                {isSettingsVisible && (
                    <>
                        <div className='mt-4 grid gap-4 lg:grid-cols-2'>
                            <div className={subPanelClass}>
                                <p className={titleClass}>Bảng chữ random</p>
                                <div className='mt-2 flex flex-wrap gap-2'>
                                    {[
                                        { label: 'Hiragana', value: 'hiragana' as const },
                                        { label: 'Katakana', value: 'katakana' as const },
                                        { label: 'Cả 2 bảng', value: 'both' as const }
                                    ].map((option) => (
                                        <Button
                                            key={option.value}
                                            type='button'
                                            onClick={() =>
                                                setSettings((current) => ({
                                                    ...current,
                                                    scriptMode: option.value
                                                }))
                                            }
                                            className={cn(
                                                'rounded-md border px-3 py-1.5 text-sm font-medium transition-colors',
                                                scriptMode === option.value
                                                    ? 'border-primary bg-primary text-primary-foreground hover:bg-primary/90'
                                                    : 'border-border bg-card text-foreground hover:bg-accent hover:text-accent-foreground'
                                            )}
                                        >
                                            {option.label}
                                        </Button>
                                    ))}
                                </div>
                            </div>

                            <div className={subPanelClass}>
                                <p className={titleClass}>Nguồn ký tự</p>
                                <div className='mt-2 flex flex-wrap gap-2'>
                                    {[
                                        { label: 'Theo a/i/u/e/o', value: 'base' as const },
                                        { label: 'Từ ghép (yoon)', value: 'yoon' as const },
                                        { label: 'Cả 2', value: 'both' as const }
                                    ].map((option) => (
                                        <Button
                                            key={option.value}
                                            type='button'
                                            onClick={() =>
                                                setSettings((current) => ({
                                                    ...current,
                                                    kanaPoolMode: option.value
                                                }))
                                            }
                                            className={cn(
                                                'rounded-md border px-3 py-1.5 text-sm font-medium transition-colors',
                                                kanaPoolMode === option.value
                                                    ? 'border-primary bg-primary text-primary-foreground hover:bg-primary/90'
                                                    : 'border-border bg-card text-foreground hover:bg-accent hover:text-accent-foreground'
                                            )}
                                        >
                                            {option.label}
                                        </Button>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {kanaPoolMode !== 'yoon' && (
                            <div className={cn(subPanelClass, 'mt-4')}>
                                <p className={titleClass}>Lọc theo nguyên âm</p>
                                <div className='mt-2 flex flex-wrap gap-2'>
                                    {vowels.map((vowel) => (
                                        <Button
                                            key={vowel}
                                            type='button'
                                            onClick={() => toggleVowel(vowel)}
                                            className={cn(
                                                'rounded-md border px-3 py-1.5 text-sm font-semibold uppercase transition-colors',
                                                selectedVowels.includes(vowel)
                                                    ? 'border-primary bg-primary text-primary-foreground hover:bg-primary/90'
                                                    : 'border-border bg-card text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                                            )}
                                        >
                                            {vowel}
                                        </Button>
                                    ))}
                                </div>
                                <div className='mt-2 flex gap-2'>
                                    <Button
                                        type='button'
                                        onClick={() =>
                                            setSettings((current) => ({
                                                ...current,
                                                selectedVowels: [...vowels]
                                            }))
                                        }
                                        variant='outline'
                                        size='sm'
                                        className='h-7 px-2'
                                    >
                                        Chọn hết
                                    </Button>
                                    <Button
                                        type='button'
                                        onClick={() =>
                                            setSettings((current) => ({
                                                ...current,
                                                selectedVowels: []
                                            }))
                                        }
                                        variant='outline'
                                        size='sm'
                                        className='h-7 px-2'
                                    >
                                        Bỏ hết
                                    </Button>
                                </div>
                            </div>
                        )}

                        <div className={cn(subPanelClass, 'mt-4')}>
                            <p className={titleClass}>Nhóm phiên âm mở rộng</p>
                            <div className='mt-2 space-y-2'>
                                {extendedGroupOptions.map((group) => (
                                    <label
                                        key={group.id}
                                        className='flex cursor-pointer items-start gap-2 rounded-md border border-transparent px-1 py-0.5 text-sm text-foreground hover:border-border hover:bg-background/70'
                                    >
                                        <Checkbox
                                            checked={selectedExtendedGroupIds.includes(group.id)}
                                            onCheckedChange={() => toggleExtendedGroup(group.id)}
                                            className='mt-0.5 border-border data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground'
                                        />
                                        <span>{group.label}</span>
                                    </label>
                                ))}
                            </div>
                            <div className='mt-2 flex gap-2'>
                                <Button
                                    type='button'
                                    onClick={() =>
                                        setSettings((current) => ({
                                            ...current,
                                            selectedExtendedGroupIds: [...extendedKanaGroupIds]
                                        }))
                                    }
                                    variant='outline'
                                    size='sm'
                                    className='h-7 px-2'
                                >
                                    Chọn hết
                                </Button>
                                <Button
                                    type='button'
                                    onClick={() =>
                                        setSettings((current) => ({
                                            ...current,
                                            selectedExtendedGroupIds: []
                                        }))
                                    }
                                    variant='outline'
                                    size='sm'
                                    className='h-7 px-2'
                                >
                                    Bỏ hết
                                </Button>
                            </div>
                        </div>

                        <div className={cn(subPanelClass, 'mt-4')}>
                            <p className={titleClass}>Kiểu câu hỏi</p>
                            <div className='mt-2 flex flex-wrap gap-2'>
                                {[
                                    { label: 'Hiện Kana, nhập Romaji', value: 'kana-to-romaji' as const },
                                    { label: 'Hiện Romaji, nhập Kana', value: 'romaji-to-kana' as const }
                                ].map((option) => (
                                    <Button
                                        key={option.value}
                                        type='button'
                                        onClick={() =>
                                            setSettings((current) => ({
                                                ...current,
                                                quizDirection: option.value
                                            }))
                                        }
                                        className={cn(
                                            'rounded-md border px-3 py-1.5 text-sm font-medium transition-colors',
                                            quizDirection === option.value
                                                ? 'border-primary bg-primary text-primary-foreground hover:bg-primary/90'
                                                : 'border-border bg-card text-foreground hover:bg-accent hover:text-accent-foreground'
                                        )}
                                    >
                                        {option.label}
                                    </Button>
                                ))}
                            </div>
                        </div>

                        <div className='mt-4 flex flex-wrap gap-3 text-sm text-muted-foreground'>
                            <p>
                                Tổng ký tự trong pool: <span className='font-semibold text-foreground'>{randomPool.length}</span>
                            </p>
                            <p>
                                Đúng: <span className='font-semibold text-foreground'>{stats.correct}</span>
                            </p>
                            <p>
                                Sai: <span className='font-semibold text-destructive'>{stats.wrong}</span>
                            </p>
                            <p>
                                Tổng lượt: <span className='font-semibold text-foreground'>{stats.total}</span>
                            </p>
                        </div>

                        {randomPool.length === 0 && (
                            <p className={cn('mt-2', dangerTextClass)}>
                                Không có ký tự phù hợp với filter hiện tại. Hãy bật thêm bảng hoặc chọn lại nguyên âm.
                            </p>
                        )}
                    </>
                )}
                {!isSettingsVisible && (
                    <p className={cn('mt-3', subtleTextClass)}>
                        Cài đặt đang ẩn. Bấm &quot;Hiện cài đặt&quot; để mở lại.
                    </p>
                )}
            </section>

            <section className={cn(panelClass, 'flex flex-col items-center')}>
                {question ? (
                    <form onSubmit={checkAnswer} className='w-full max-w-md flex flex-col items-center'>
                        <p className='text-xs font-semibold uppercase tracking-[0.16em] text-foreground'>
                            {question.script}
                        </p>
                        <p className='mt-2 text-sm uppercase tracking-widest text-muted-foreground'>
                            {quizDirection === 'kana-to-romaji' ? 'Ký tự hiển thị' : 'Romaji hiển thị'}
                        </p>
                        <p className='mt-2 text-7xl leading-none font-bold text-foreground sm:text-8xl'>{promptValue}</p>

                        <label className='mt-5 block'>
                            <span className='text-sm font-semibold text-foreground'>
                                {quizDirection === 'kana-to-romaji' ? 'Nhập romaji' : 'Nhập kana'}
                            </span>
                            <Input
                                ref={answerInputRef}
                                type='text'
                                value={answer}
                                onChange={(event) => setAnswer(event.target.value)}
                                disabled={isAudioPlaying}
                                autoComplete='off'
                                placeholder={quizDirection === 'kana-to-romaji' ? 'Ví dụ: shi' : 'Ví dụ: し'}
                                className='mt-2 w-full text-base'
                            />
                        </label>

                        <div className='mt-4 flex flex-wrap gap-2'>
                            <Button type='submit' disabled={isAudioPlaying}>
                                Kiểm tra
                            </Button>
                            <Button
                                type='button'
                                onClick={handleNextQuestion}
                                variant='secondary'
                            >
                                Câu mới
                            </Button>
                            <Button
                                type='button'
                                onClick={() => setShowAnswer(true)}
                                className='rounded-md border px-4 py-2 text-sm font-semibold'
                            >
                                Hiện đáp án
                            </Button>
                        </div>

                        {resultMessage && (
                            <p
                                className={cn(
                                    'mt-3 text-sm font-semibold',
                                    resultState === 'correct'
                                        ? 'text-foreground'
                                        : 'text-destructive'
                                )}
                            >
                                {resultMessage}
                            </p>
                        )}

                        {showAnswer && (
                            <div className='mt-4 rounded-lg border border-border bg-muted/40 p-3 text-sm'>
                                <p className='text-foreground'>
                                    Kana đúng: <span className='text-xl font-semibold'>{question.cell.kana}</span>
                                </p>
                                <p className='mt-1 text-foreground'>
                                    Phát âm (romaji): <span className='font-semibold'>{normalizeRomaji(question.cell.romaji)}</span>
                                </p>

                                <Button
                                    type='button'
                                    onClick={handlePlayAudio}
                                    disabled={isAudioPlaying || !audioPath}
                                    variant='secondary'
                                    size='sm'
                                    className='mt-3'
                                >
                                    {isAudioPlaying ? 'Đang phát, chờ hết...' : 'Nghe phát âm'}
                                </Button>
                                {audioError && <p className='mt-2 text-xs text-destructive'>{audioError}</p>}
                            </div>
                        )}
                    </form>
                ) : (
                    <div>
                        <p className='text-base text-muted-foreground'>
                            Chưa có ký tự phù hợp. Hãy cập nhật settings để bắt đầu.
                        </p>
                    </div>
                )}
            </section>
        </main>
    )
}
